/**
 * StepProviderSelect — First step of the datasource wizard.
 *
 * Fetches providers via useProviders(), groups by category,
 * and renders ProviderCard per manifest. Non-enrolled providers
 * show disabled with amber badge and deep-link CTA.
 */

import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { Skeleton } from '@/components/ui';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useProviders } from '../../hooks/use-providers';
import { DatasourceProviderIcon } from '../ProviderIcons';
import type { EnrichedProviderManifest } from '../../types';

interface StepProviderSelectProps {
  onSelect: (manifest: EnrichedProviderManifest) => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  cloud_storage: 'datasources.wizard.categories.cloudStorage',
  version_control: 'datasources.wizard.categories.versionControl',
  collaboration: 'datasources.wizard.categories.collaboration',
};

const CATEGORY_ORDER = ['cloud_storage', 'version_control', 'collaboration'];

function groupByCategory(manifests: EnrichedProviderManifest[]): Record<string, EnrichedProviderManifest[]> {
  const groups: Record<string, EnrichedProviderManifest[]> = {};
  for (const m of manifests) {
    const cat = m.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(m);
  }
  return groups;
}

export function StepProviderSelect({ onSelect }: StepProviderSelectProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data: providers, isLoading, error } = useProviders();

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((j) => (
                <Skeleton key={j} className="h-32 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error || !providers) {
    return (
      <div className="text-center py-12 text-red-600 dark:text-red-400">
        {t('datasources.wizard.loadProvidersFailed')}
      </div>
    );
  }

  const grouped = groupByCategory(providers);
  const sortedCategories = CATEGORY_ORDER.filter((c) => grouped[c]);

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('datasources.wizard.selectProvider')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('datasources.wizard.selectProviderHint')}
        </p>
      </div>

      {sortedCategories.map((category) => (
        <div key={category}>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            {t(CATEGORY_LABELS[category] ?? category)}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped[category].map((manifest) => (
              <ProviderCard
                key={manifest.provider_type}
                manifest={manifest}
                onSelect={onSelect}
                onSetup={(url) => navigate(url + '?returnTo=/knowledge/datasources/new')}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// ProviderCard
// =============================================================================

interface ProviderCardProps {
  manifest: EnrichedProviderManifest;
  onSelect: (manifest: EnrichedProviderManifest) => void;
  onSetup: (url: string) => void;
}

function ProviderCard({ manifest, onSelect, onSetup }: ProviderCardProps) {
  const { t } = useI18n();
  // Availability (ISS-380/383): 'ready' = available; 'needs_setup' and
  // 'needs_install' = requires additional configuration.
  const available = manifest.enrolled && manifest.enrollment_status === 'ready';
  const disabled = !available;

  const badge = available
    ? {
        label: t('datasources.wizard.available'),
        className:
          'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300',
        Icon: CheckCircle2,
      }
    : {
        label:
          manifest.enrollment_status === 'needs_install'
            ? t('datasources.wizard.needsInstallation')
            : t('datasources.wizard.needsSetup'),
        className:
          'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
        Icon: AlertTriangle,
      };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(manifest)}
      className={`relative flex flex-col items-start gap-3 p-4 rounded-lg border text-left transition-all ${
        disabled
          ? 'opacity-60 border-dashed border-amber-300 dark:border-amber-600 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50'
          : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-md cursor-pointer bg-white dark:bg-gray-800'
      }`}
    >
      {/* Availability / enrollment badge */}
      <span
        className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
      >
        <badge.Icon className="w-3 h-3" />
        {badge.label}
      </span>

      {/* Icon + Name */}
      <div className="flex items-center gap-3">
        <DatasourceProviderIcon providerType={manifest.provider_type} size={28} />
        <span className="font-medium text-gray-900 dark:text-white">
          {manifest.display_name}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
        {manifest.description}
      </p>

      {/* Setup CTA */}
      {disabled && manifest.enrollment_action && (
        <span
          role="link"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onSetup(manifest.enrollment_action!.url);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              onSetup(manifest.enrollment_action!.url);
            }
          }}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer mt-auto"
        >
          {t(manifest.enrollment_action.label)}
        </span>
      )}
    </button>
  );
}
